import { styled } from "styled-components";
import { BackButton } from "../../../components/layout/back-button/back-button.component";
import { colors } from "../../../theme/colors";
import { Link, useParams } from "react-router-dom";
import { Column } from "../../../components/ui/grid/column.component";
import { Row } from "../../../components/ui/grid/row.component";
import {
  MdChevronRight,
  MdEdit,
  MdPlayArrow,
  MdVideocam,
} from "react-icons/md";
import { Spacer } from "../../../components/ui/spacer/spacer.component";
import { Pill } from "../../../components/ui/pill/pill.component";
import { Button, Card, Tabs, Spin } from "antd";
import _ from "lodash";
import { ViewLayout } from "../../films/sections/view.layout";
import { CastView } from "../components/cast-view.component";
import { ProjectKind } from "../../../enums/ProjectKind";
import { useEffect, useState, Fragment } from "react";
import { axios } from "../../../api/axios";
import { SeriesProfile } from "../../../models/series-profile.model";
import { Distribution } from "../../../models/distribution.model";
import { handleResponse } from "../../../utils/functions/http/handleResponse";
import { AxiosError } from "axios";
import { useAtomValue } from "jotai";
import { masterAtom } from "../../../atoms/masters.atom";
import { valueToLabel } from "../../../utils/functions/misc/valueToLabel";
import { mediaUrl } from "../../../utils/functions/media/mediaUrl";
import { episodeDurationParts } from "../utils/duration";

export const ViewSeries = () => {
  const { id, customURL } = useParams();
  const seriesKey = id || customURL;
  const master = useAtomValue(masterAtom);

  const [loading, setLoading] = useState(true);
  const [series, setSeries] = useState<SeriesProfile | null>(null);
  const [distributions, setDistributions] = useState<Distribution[]>([]);
  const [hasFetchedDistribution, setHasFetchedDistribution] = useState(false);

  // Newest poster of each ratio. An older blank card must not hide a later upload.
  const posterSrc = (ratios: string[]) =>
    mediaUrl(
      [...(series?.series_image ?? [])]
        .reverse()
        .find((img) => ratios.includes(img.aspect_ratio))?.image
    );
  const verticalPoster = posterSrc(["3_4", "4_5", "9_16", "1_1"]);
  const horizontalPoster = posterSrc(["16_9", "4_3", "2_39_1"]);

  // Get first season's first episode for technical details
  const firstEpisode = series?.seasons?.[0]?.episodes?.[0];

  // Collect all cast and crew from all episodes
  const allCast = series?.seasons?.flatMap((season) =>
    season.episodes.flatMap((episode) =>
      (episode as any).episode_cast || episode.cast || []
    )
  ) || [];

  const allCrew = series?.seasons?.flatMap((season) =>
    season.episodes.flatMap((episode) =>
      (episode as any).episode_crew || episode.crew || []
    )
  ) || [];

  // Extract directors and producers from crew
  const directors = allCrew.filter(
    (member) =>
      member.role?.toLowerCase() === "director" ||
      member.role?.toLowerCase() === "directors"
  );

  const producers = allCrew.filter(
    (member) =>
      member.role?.toLowerCase() === "producer" ||
      member.role?.toLowerCase() === "producers"
  );

  const otherCrew = allCrew.filter(
    (member) =>
      member.role?.toLowerCase() !== "director" &&
      member.role?.toLowerCase() !== "directors" &&
      member.role?.toLowerCase() !== "producer" &&
      member.role?.toLowerCase() !== "producers"
  );

  // Collect all awards from all seasons
  const allAwards = series?.seasons?.flatMap((season) => season.awards || []) || [];

  useEffect(() => {
    if (!seriesKey) return;

    setLoading(true);
    axios
      .get<SeriesProfile>("/client/series", {
        headers: {
          "X-SERIES-ID": seriesKey,
        },
      })
      .then((response) => {
        setSeries(response.data);
      })
      .catch(handleResponse)
      .finally(() => setLoading(false));
  }, [seriesKey]);

  useEffect(() => {
    if (!series?.id || !series.is_distributed || hasFetchedDistribution) return;

    axios
      .get<Distribution | Distribution[]>("/client/distribution", {
        headers: {
          "X-Project-Id": series.id,
          "X-Project-Type": "series",
        },
      })
      .then((response) => {
        setHasFetchedDistribution(true);
        setDistributions(Array.isArray(response.data) ? response.data : [response.data]);
      })
      .catch((error) => {
        if (error instanceof AxiosError && error.response?.status === 404) return;
        return handleResponse(error);
      });
  }, [series, hasFetchedDistribution]);

  if (loading) {
    return (
      <LoadingContainer>
        <Spin size="large" />
      </LoadingContainer>
    );
  }

  if (!series) {
    return (
      <LoadingContainer>
        <p>Series not found</p>
      </LoadingContainer>
    );
  }

  const episodeCount =
    series.seasons?.reduce(
      (total, season) => total + (season.episodes?.length ?? 0),
      0
    ) ?? 0;
  const runtimeSeconds =
    series.seasons?.reduce((total, season) => {
      return (
        total +
        (season.episodes ?? []).reduce((episodeTotal, episode) => {
          const parts = episodeDurationParts(episode.duration);
          const minutes = Number(parts.mm || 0);
          const seconds = Number(parts.ss || 0);
          if (Number.isNaN(minutes) || Number.isNaN(seconds)) return episodeTotal;
          return episodeTotal + minutes * 60 + seconds;
        }, 0)
      );
    }, 0) ?? 0;
  const runtimeLabel = `${Math.floor(runtimeSeconds / 60)}m ${String(
    runtimeSeconds % 60
  ).padStart(2, "0")}s`;

  return (
    <ViewLayout
      customUrl={series.custom_name || seriesKey}
      id={series.id}
      kind={ProjectKind.Series}
      title={series.title}
      episodeCount={episodeCount}
      distributed={!!series.is_distributed}
      status={series.status}
      details={[
        valueToLabel("languages", series.language, master)?.toString() ?? "",
        runtimeLabel,
      ]}
    >
      <Row style={{ width: "100%" }}>
        <VerticalPoster
          src={verticalPoster || "/images/ui/bg-placeholder.png"}
          alt=""
        />
        <HorizontalPoster
          src={horizontalPoster || "/images/ui/bg-placeholder.png"}
          alt=""
        />
      </Row>
      <Spacer size={10} direction="bottom" />
      <Row $gap={5}>
        {series.genre?.map((genre, index) => (
          <Pill key={index} style={{ backgroundColor: colors.primary.hex }}>
            {_.capitalize(genre)}
          </Pill>
        ))}
      </Row>
      <Spacer size={20} direction="bottom" />
      <Row $gap={32} style={{ width: "100%", alignItems: "flex-start" }}>
        <TabsPane>
          <Tabs
            defaultActiveKey="1"
            items={[
            {
              key: "1",
              label: "Synopsis",
              children: (
                <Synopsis>
                  {series.synopsis || "No synopsis available."}
                </Synopsis>
              ),
            },
            {
              key: "2",
              label: "Censor Ratings",
              children: (
                <Synopsis>
                  <b>Language:</b>{" "}
                  {valueToLabel("language_ratings", series.language_rating, master) ||
                    "Not rated"}
                  <br />
                  <b>Violence:</b>{" "}
                  {valueToLabel("violence_ratings", series.violence_rating, master) ||
                    "Not rated"}
                  <br />
                  <b>Adult Content:</b>{" "}
                  {valueToLabel(
                    "adult_content_ratings",
                    series.adult_content_rating,
                    master
                  ) || "Not rated"}
                  <br />
                  <b>Drugs:</b>{" "}
                  {valueToLabel("drugs_ratings", series.drug_rating, master) ||
                    "Not rated"}
                  <br />
                  <b>Harmful / Dangerous Act:</b>{" "}
                  {valueToLabel("harmful_ratings", series.harmful_rating, master) ||
                    "Not rated"}
                  <br />
                  <b>Hateful and Derogatory:</b>{" "}
                  {valueToLabel("hateful_ratings", series.hateful_rating, master) ||
                    "Not rated"}
                </Synopsis>
              ),
            },
            ...(distributions.some((item) => item.videos?.length)
              ? [
                  {
                    key: "6",
                    label: "Videos",
                    children: (
                      <Row
                        style={{
                          gap: "1rem",
                          overflowX: "scroll",
                          maxWidth: "90%",
                        }}
                      >
                        {distributions.flatMap((item) =>
                          (item.videos ?? []).map((video) => {
                            const category =
                              (video as { video_category?: string; type?: string })
                                .video_category ||
                              (video as { type?: string }).type;
                            const episodeNumber = (
                              video as { episode_number?: number }
                            ).episode_number;
                            const title =
                              video.video_type === "primary-trailer" &&
                              category === "series"
                                ? "Series trailer"
                                : video.video_type === "additional-trailer" &&
                                    category === "series"
                                  ? "Series vertical trailer"
                                  : video.video_type === "primary-trailer"
                                    ? "Season trailer"
                                    : video.video_type === "additional-trailer"
                                      ? "Season vertical trailer"
                                      : video.video_type === "primary-video"
                                        ? `Episode ${episodeNumber ?? ""} video`
                                        : `Episode ${episodeNumber ?? ""} vertical video`;
                            return (
                              <Card
                                key={`${category}-${video.video_type}-${episodeNumber ?? "series"}-${video.video_file_name}`}
                                style={{
                                  minWidth: video.video_id ? "45rem" : "15rem",
                                  maxWidth: "45rem",
                                  position: "relative",
                                }}
                                title={title}
                                size="small"
                              >
                                {video.video_id ? (
                                  <iframe
                                    src={`https://iframe.mediadelivery.net/embed/102339/${video.video_id}?autoplay=false`}
                                    loading="lazy"
                                    style={{
                                      border: "none",
                                      width: "100%",
                                      height: "25rem",
                                    }}
                                    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                                    allowFullScreen
                                  />
                                ) : (
                                  <Synopsis>{video.video_file_name || "Uploaded"}</Synopsis>
                                )}
                              </Card>
                            );
                          })
                        )}
                      </Row>
                    ),
                  },
                ]
              : []),
            ...(distributions.find((item) => item.agreement_url)?.agreement_url
              ? [
                  {
                    key: "7",
                    label: "Agreement",
                    children: (
                      <iframe
                        src={
                          distributions.find((item) => item.agreement_url)
                            ?.agreement_url
                        }
                        title="Distribution agreement"
                        style={{
                          width: "50vw",
                          height: "70.7vw",
                          minHeight: "800px",
                        }}
                      />
                    ),
                  },
                ]
              : []),
          ]}
        />
        </TabsPane>
        <Column $gap={12} style={{ flex: "0 0 200px", width: "200px" }}>
          <ShootingFormat>
            <MdVideocam color={colors.white.hex} size={120} />
            <span>
              {valueToLabel(
                "shooting_format",
                firstEpisode?.shooting_format,
                master
              ) || "N/A"}
              <br />
              {valueToLabel("aspect_ratio", firstEpisode?.aspect_ratio, master) ||
                "N/A"}
            </span>
          </ShootingFormat>
          <TechnicalDetailsText>
            Language: <span>{_.capitalize(series.language) || "N/A"}</span>
          </TechnicalDetailsText>
        </Column>
      </Row>
      <Spacer size={20} direction="bottom" />

      {/* Episodes Section */}
      <SectionTitle to={`/s/${series.custom_name || seriesKey}/episodes`}>
        Episodes&nbsp;
        <MdChevronRight />
      </SectionTitle>
      <Spacer size={10} direction="bottom" />
      <Row style={{ gap: "1rem", flexWrap: "wrap", width: "100%", maxWidth: "100%" }}>
        {series.seasons?.flatMap((season, seasonIndex) =>
          season.episodes.map((episode, episodeIndex) => (
            <Card
              key={`${seasonIndex}-${episodeIndex}`}
              style={{ flex: "1 1 28rem", minWidth: "min(100%, 28rem)", maxWidth: "100%" }}
              title={`Episode ${episode.episode_number}${episode.title ? ` - ${episode.title}` : ""
                }`}
              size="small"
            >
              <Spacer size={-5} direction="bottom" />
              <Row style={{ gap: "1rem" }}>
                <Pill
                  style={{
                    backgroundColor: colors.primary.hex,
                    width: "fit-content",
                    color: colors.white.hex,
                    fontWeight: 600,
                    textWrap: "nowrap",
                  }}
                >
                  {season.season_title || `Season ${season.season_number}`}
                </Pill>
                <Synopsis style={{ color: colors.text.hex, fontSize: "1.3rem" }}>
                  {_.truncate(
                    (episode as any).synopsis || "No description available.",
                    { length: 75 }
                  )}
                </Synopsis>
              </Row>
            </Card>
          ))
        )}
        {!series.seasons?.length && (
          <Synopsis>No episodes available.</Synopsis>
        )}
      </Row>
      <Spacer size={30} direction="bottom" />

      {/* Awards Section */}
      {allAwards.length > 0 ? (
        <Column style={{ width: "100%" }}>
          <SectionTitle to={`/s/${series.custom_name || seriesKey}/awards`}>Awards</SectionTitle>
          <Spacer size={10} direction="bottom" />
          <Row style={{ gap: "1rem", flexWrap: "wrap", width: "100%", maxWidth: "100%" }}>
            {allAwards.map((award, index) => (
              <Card
                key={index}
                style={{
                  flex: "1 1 28rem",
                  minWidth: "min(100%, 28rem)",
                  maxWidth: "100%",
                  position: "relative",
                }}
                title={`${award.award_category || "Award"} - ${award.name_of_festival || "Unknown"
                  }`}
                size="small"
              >
                {award.award_winner_name || "Unknown"} •{" "}
                {String(award.award_year || award.year || "N/A")} •{" "}
                {award.country || "N/A"}
              </Card>
            ))}
          </Row>
          <Spacer size={30} direction="bottom" />
        </Column>
      ) : (
        <Spacer size={0} direction="bottom" />
      )}

      {/* Directors and Producers Section - Side by Side */}
      {((directors.length > 0 || producers.length > 0) ? (
        <Fragment>
          <Row style={{ width: "100%", gap: "2rem", alignItems: "flex-start", flexWrap: "wrap" }}>
            {/* Directors Column */}
            {directors.length > 0 ? (
              <Column style={{ flex: "1 1 24rem", minWidth: "min(100%, 24rem)", maxWidth: "100%" }}>
                <SectionTitle to={`/s/${series.custom_name || seriesKey}/cast-crew#directors`}>
                  Directors
                </SectionTitle>
                <Spacer size={10} direction="bottom" />
                <Row style={{ gap: "2rem", flexWrap: "wrap", maxWidth: "100%" }}>
                  {_.uniqBy(directors, "name").map((director, index) => (
                    <CastView
                      key={index}
                      name={director.name}
                      description={director.biography || ""}
                    />
                  ))}
                </Row>
              </Column>
            ) : (
              <Column style={{ flex: 1 }} />
            )}

            {/* Producers Column */}
            {producers.length > 0 ? (
              <Column style={{ flex: "1 1 24rem", minWidth: "min(100%, 24rem)", maxWidth: "100%" }}>
                <SectionTitle to={`/s/${series.custom_name || seriesKey}/cast-crew#producers`}>
                  Producers
                </SectionTitle>
                <Spacer size={10} direction="bottom" />
                <Row style={{ gap: "2rem", flexWrap: "wrap", maxWidth: "100%" }}>
                  {_.uniqBy(producers, "name").map((producer, index) => (
                    <CastView
                      key={index}
                      name={producer.name}
                      description={producer.biography || ""}
                    />
                  ))}
                </Row>
              </Column>
            ) : (
              <Column style={{ flex: 1 }} />
            )}
          </Row>
          <Spacer size={30} direction="bottom" />
        </Fragment>
      ) : (
        <Spacer size={0} direction="bottom" />
      )) as any}

      {/* Cast Section */}
      {allCast.length > 0 ? (
        <Column style={{ width: "100%" }}>
          <SectionTitle to={`/s/${series.custom_name || seriesKey}/cast-crew#cast`}>Cast</SectionTitle>
          <Spacer size={10} direction="bottom" />
          <Row style={{ gap: "2rem", overflowX: "scroll", maxWidth: "100%" }}>
            {_.uniqBy(allCast, "name").map((castMember, index) => (
              <CastView
                key={index}
                name={castMember.name}
                description={castMember.character || ""}
              />
            ))}
          </Row>
          <Spacer size={30} direction="bottom" />
        </Column>
      ) : (
        <Spacer size={0} direction="bottom" />
      )}

      {/* Crew Section */}
      {otherCrew.length > 0 ? (
        <Column style={{ width: "100%" }}>
          <SectionTitle to={`/s/${series.custom_name || seriesKey}/cast-crew#crew`}>Crew</SectionTitle>
          <Spacer size={10} direction="bottom" />
          <Row style={{ gap: "2rem", overflowX: "scroll", maxWidth: "100%" }}>
            {_.uniqBy(otherCrew, "name").map((crewMember, index) => (
              <CastView
                key={index}
                name={crewMember.name}
                description={_.capitalize(crewMember.role) || ""}
              />
            ))}
          </Row>
        </Column>
      ) : (
        <Spacer size={0} direction="bottom" />
      )}
    </ViewLayout>
  );
};

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100vw;
  height: 100vh;
  background-color: ${colors.lightBackground.hex};
  color: ${colors.text.hex};
`;

const Main = styled.div`
  position: relative;
  width: 100vw;
  height: 100vh;
  overflow-x: hidden;

  color: ${colors.white.hex};
  padding: 3.5rem 10rem;

  & .ant-tabs-tab-btn {
    color: #f2f7fc;
  }
`;

const BackgroundImage = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url("/images/live/friends.png");
  background-size: cover;
  z-index: -1;
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${colors.text.withOpacity(0.8).hex};
  backdrop-filter: blur(30px);
  z-index: -1;
`;

const DetailsLink = styled(Link)`
  font-size: 1.5rem;
  color: ${colors.white.hex};
  font-weight: 600;
  text-decoration: none;

  & span {
    transition: all 0.3s;
    display: inline-flex;
    align-items: center;
    font-weight: 500;
  }

  &:hover {
    text-decoration: underline;

    & span {
      color: ${colors.primary.hex};
    }
  }
`;

const Title = styled.h1`
  font-size: 4.5rem;
  font-weight: 500;
`;

const Details = styled.h3`
  font-size: 1.5rem;
  font-weight: 400;
  opacity: 60%;
`;

const Poster = styled.img`
  border-radius: 0.75rem;
  height: 35rem;
  width: 100%;
  object-fit: cover;
  object-position: center;
  background-color: #07121d;
`;

const VerticalPoster = styled(Poster)`
  flex: 0.25;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
`;

const HorizontalPoster = styled(Poster)`
  flex: 0.75;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
`;

const Synopsis = styled.p`
  font-size: 1.5rem;
  color: ${colors.white.hex};
  font-weight: 400;
`;

const TabsPane = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  max-width: 100%;
`;

const ShootingFormat = styled.div`
  position: relative;
  width: 120px;
  height: 120px;
  flex-shrink: 0;
  font-size: 1.3rem;
  line-height: 1.3;
  color: ${colors.text.hex};
  font-weight: 600;
  text-align: center;

  & > svg,
  & > span {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  & > span {
    width: 100%;
    transform: translate(-50%, -50%) translateX(-0.8rem);
  }
`;

const SectionTitle = styled(Link)`
  display: inline-flex;
  align-items: center;
  font-size: 2.5rem;
  font-weight: 600;
  border-left: 3px solid ${colors.primary.hex};
  padding-left: 1.2rem;
  color: ${colors.white.hex};
  text-decoration: none;

  & svg {
    transition: all 0.4s;
    transform: translateY(0.15rem);
  }

  &:hover svg {
    transform: translateY(0.15rem) translateX(0.5rem);
  }
`;

const TrailerThumbnail = styled.img`
  width: 100%;
  border-radius: 1rem;
`;

const PlayButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) translateY(1.5rem);
  width: 5rem;
  height: 5rem;
  background-color: ${colors.grey.withOpacity(0.5).hex};
  backdrop-filter: blur(10px);
  border-radius: 50%;
  cursor: pointer;
`;

const TechnicalDetailsText = styled.h3`
  font-size: 1.5rem;
  font-weight: 500;
  text-align: center;

  & span {
    font-weight: 600;
    color: ${colors.primary.hex};
  }
`;
